import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Flex, Box, Image } from 'rebass';
import { FiRefreshCcw, FiCheckCircle } from 'react-icons/fi';
// import { saveAs } from 'file-saver';
import Grid from 'app/grid/index';
import { checkIsIpadUser, useCheckIsIosUser, useLandScapeCheckHook } from 'story-media/hooks';
import CirclePlayButton from './CirclePlayButton';
import ButtonWithIcon from './ButtonWithIcon';
import { Duration } from './RecordVideo';
import { useTimer } from 'react-timer-hook';
import { useWindowSize } from 'react-use';

export const ControlButtons = ({ onReject, rejectLabel = "Reshoot", onAccept, handlePlayPause, isPlaying, ...props }) => {
  const { width, height } = useWindowSize();
  return <Flex
    alignItems="center"
    justifyContent="space-between"
    p={[2, 3]}
    backgroundColor="white"
    width="100%"
    sx={width < 500 ? {
      position: 'fixed', bottom: 0, left: '50%', right: 0, backgroundColor: '#fff', zIndex: 1,
      transform: 'translate(-50%, 0%)',
      maxWidth: '480px', width: '100%'
    } : width < 1025 ? {
      position: 'absolute', bottom: 0, left: 0, backgroundColor: '#fff', zIndex: 1,
    } : {}}
  >
    <Flex sx={{ gridRowStart: [2, 1] }} justifyContent="flex-end">
      <ButtonWithIcon type="button" icon={FiRefreshCcw} onClick={onReject}
        sx={{
          height: 44,
          borderRadius: '23px',
          border: '1.5px solid #9B59B6',
          bg: 'transparent',
          color: '#9B59B6',
          fontFamily: 'Poppins',
          fontSize: 14,
          fontWeight: 500,
          letterSpacing: '-0.084px',
          lineHeight: '20px'
        }}
      >
        {rejectLabel}
      </ButtonWithIcon>
    </Flex>
    <Flex
      sx={{ gridRowStart: 1, gridColumn: ['1 / span 2', 'inherit'] }}
      justifyContent="center"
    >
      <CirclePlayButton size={44} isPlaying={isPlaying} onClick={handlePlayPause} bg="#9B59B6" />
      <Duration {...props} />
    </Flex>
    <Flex sx={{ gridRowStart: [2, 1] }}>
      <ButtonWithIcon
        type="button"
        icon={FiCheckCircle}
        variant="special"
        onClick={() => {
          onAccept();
        }}
        sx={{
          height: 44,
          borderRadius: '23px',
          background: '#9B59B6',
          color: '#fff',
          fontFamily: 'Poppins',
          border: 0,
          fontSize: 16,
          fontWeight: 500,
          letterSpacing: '-0.084px',
          lineHeight: '20px'
        }}
      >
        Upload
      </ButtonWithIcon>
    </Flex>
  </Flex >
}

function VideoPreview({
  blob,
  onReject,
  onAccept,
  height,
  mt,
  videoTime,
  ...props
}) {

  const videoRef = useRef(null);
  const [objectURL, setObjectURL] = useState(undefined);
  const [isPlaying, setIsPlaying] = useState(false);
  const [gridHeight, setGridHeight] = useState(height);

  const isIOSUser = useCheckIsIosUser();
  const isIpadUser = checkIsIpadUser();
  const [isLandscape] = useLandScapeCheckHook();
  const expiryTimestamp = new Date();
  expiryTimestamp.setSeconds(expiryTimestamp.getSeconds() + videoTime);

  const {
    seconds,
    minutes,
    hours,
    start,
    pause,
  } = useTimer({ expiryTimestamp, autoStart: false, onExpire: () => console.warn('onExpire called'), interval: 20 });

  useEffect(() => {
    if (isLandscape) setGridHeight('100vh');
    else setGridHeight(height);
  }, [isLandscape]);

  useEffect(() => {
    if (!objectURL) {
      setObjectURL(URL.createObjectURL(blob));
    }
    return () => {
      URL.revokeObjectURL(objectURL);
    };
  }, [objectURL, blob]);

  const handlePlayPause = useCallback(() => {
    if (!isPlaying) {
      setIsPlaying(true);
      videoRef.current.play();
      start()
    }
    if (isPlaying) {
      setIsPlaying(false);
      videoRef.current.pause();
      pause()
    }
  }, [isPlaying, videoRef, setIsPlaying]);

  return (
    <Box mt={mt} textAlign="center">
      <Grid id="video-preview" {...props} height={gridHeight}>
        <Image
          ref={videoRef}
          as="video"
          src={objectURL}
          // autoPlay={isIOSUser}
          height={isIpadUser || isIOSUser ? '100vh' : '100%'}
          width="100%"
          onPause={() => {
            setIsPlaying(false)
            pause()
          }}
          onEnded={() => {
            setIsPlaying(false);
            videoRef.current.currentTime = 0;
            pause()
          }}
          sx={{ objectFit: 'contain', bg: 'black', gridArea: '1 / 1 / 2 / 2' }}
        >
          <source src={objectURL} />
          <p>Your browser cannot preview this video type</p>
          <p>
            Once uploaded, the video will be converted for inclusion with the celebration video.
          </p>
        </Image>
        <Flex
          alignItems="flex-end"
          justifyContent="center"
          // sx={{ gridArea: '1 / 1 / 2 / 2', zIndex: 1 }}
          mb={isIOSUser && '64px'}
        >
          <ControlButtons hours={hours} seconds={seconds} minutes={minutes} isPlaying={isPlaying} handlePlayPause={handlePlayPause} onReject={onReject} onAccept={onAccept} />
        </Flex>
      </Grid>
    </Box>
  );
}
VideoPreview.propTypes = {
  onAccept: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  blob: PropTypes.shape({ type: PropTypes.string }).isRequired,
  height: PropTypes.string,
};
VideoPreview.defaultProps = { height: 360 };

export default VideoPreview;
